import dayjs from 'dayjs';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector, useStore } from 'react-redux';

import { DatePicker } from '@/components/common/DatePicker';
import {
  ELocationSearchAutocompleteListVariant,
  LocationSearchAutocompleteList,
} from '@/components/common/LocationSearchAutocompleteList';
import DropdownButton from '@/components/route/search/DropdownButton/DropdownButton';
import { EHomeSearchWidgetVariant } from '@/components/ui/HomeSearchWidget/HomeSearchWidget';
import {
  LocationAutocompleteProvider,
  useLocationAutocompleteContext,
} from '@/components/ui/HomeSearchWidget/LocationAutocompleteContext';
import {
  LocationFilterProvider,
  useLocationFilterContext,
} from '@/components/ui/HomeSearchWidget/LocationFilterContext';
import { ESearchFilters } from '@/constants/searchFilters';
import { applySearchFilter } from '@/redux/modules/search';
import { getQueryParams } from '@/redux/selectors/queryParams';
import { calculateOptionIndex } from '@/utility/autoCompleteGroupedOptions';

import { useAiSearch } from './AiSearchContext';

/**
 * Airbnb-style Where / When / Guests pills for the AI-search experience, shown
 * at the START of the /rv-search filter row. The AI box owns the fuzzy stuff
 * (type, amenities, price, vibes…); these give renters first-class, correctable
 * control over the two high-commitment inputs plus party size.
 *
 * Self-contained by design: each pill reads the current value straight from the
 * URL query params (the search page's source of truth) and commits via
 * applySearchFilter — no dependency on the stateful header-widget machinery or
 * the core FilterPills modal config. The AI box already fills these params when
 * a renter types a location/date, so the pills reflect that automatically.
 *
 * NOTE (follow-up): the location pill is a plain text input for now; wiring the
 * mapbox autocomplete used elsewhere is a later polish pass.
 */

function str(v: unknown): string {
  if (Array.isArray(v)) return typeof v[0] === 'string' ? v[0] : '';
  return typeof v === 'string' ? v : '';
}

type TPopoverPill = {
  title: string;
  active: boolean;
  open: boolean;
  onToggle: () => void;
  onClose: () => void;
  children: React.ReactNode;
};

/**
 * Reuses the search page's DropdownButton for the pill (exact match with the
 * other filter pills), with a panel portaled to <body> so it isn't clipped by
 * the filter row's overflow. Positioned fixed under the pill; outside-click or
 * Escape closes.
 */
function PopoverPill({ title, active, open, onToggle, onClose, children }: TPopoverPill) {
  const anchorRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });

  useLayoutEffect(() => {
    if (!open || !anchorRef.current) return;
    const r = anchorRef.current.getBoundingClientRect();
    setPos({ top: r.bottom + 8, left: r.left });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (anchorRef.current?.contains(t) || panelRef.current?.contains(t)) return;
      onClose();
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  return (
    <>
      <div ref={anchorRef} className="flex items-center">
        <DropdownButton title={title} active={active} selected={open} onClick={onToggle} />
      </div>
      {open &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            ref={panelRef}
            style={{ position: 'fixed', top: pos.top, left: pos.left, zIndex: 1300 }}
            className="p-4 bg-white border shadow-xl rounded-2xl border-neutral-20">
            {children}
          </div>,
          document.body,
        )}
    </>
  );
}

/**
 * Reuses the real mapbox location autocomplete (same machinery as the home
 * search widget). A selection writes address + mapbox ids to globalHeader;
 * onDataSelection then reads the freshest globalHeader and commits to the search.
 */
/**
 * Compact location input: spreads the autocomplete context's inputProps onto a
 * normal-height field (the shared LocationFilterInput is hard-coded to the tall
 * 100px search-bar layout, which looks huge in a popover).
 */
function CompactLocationInput() {
  const { inputProps } = useLocationAutocompleteContext();
  const { setAddressValue, setMapboxValues } = useLocationFilterContext();
  return (
    <div className="relative">
      <input
        {...inputProps}
        placeholder="Where to?"
        className="w-full px-4 text-sm border rounded-lg py-2.5 pr-9 border-neutral-30 focus:outline-none focus:border-gray-900"
      />
      {inputProps.value ? (
        <button
          type="button"
          aria-label="Clear location"
          onClick={() => {
            setAddressValue('');
            setMapboxValues('', '');
          }}
          className="absolute text-gray-400 -translate-y-1/2 right-3 top-1/2 hover:text-gray-700">
          ✕
        </button>
      ) : null}
    </div>
  );
}

function WhereAutocomplete({ onApply }: { onApply: () => void }) {
  return (
    <LocationFilterProvider variant={EHomeSearchWidgetVariant.RvRentals}>
      <LocationAutocompleteProvider
        variant={EHomeSearchWidgetVariant.RvRentals}
        isOpened
        onOpen={() => undefined}
        onDataSelection={onApply}>
        <div className="w-80">
          <CompactLocationInput />
          <InlineLocationMenu />
        </div>
      </LocationAutocompleteProvider>
    </LocationFilterProvider>
  );
}

/**
 * The suggestion list rendered INLINE in the popover (vertical layout) instead
 * of LocationAutocompleteMenu, which wraps itself in DesktopFilterContent and
 * positions a wide dropdown against the home search bar.
 */
function InlineLocationMenu() {
  const { groupedOptions, allGroupedOptions, getOptionProps } = useLocationAutocompleteContext();
  if (!groupedOptions?.length) return null;
  return (
    <div className="mt-2 overflow-auto max-h-80">
      <LocationSearchAutocompleteList
        groupedOptions={groupedOptions}
        getOptionProps={({ option, index }) =>
          getOptionProps({
            option,
            index: calculateOptionIndex(option.groupName, index, allGroupedOptions),
          })
        }
        variant={ELocationSearchAutocompleteListVariant.Vertical}
      />
    </div>
  );
}

export default function AiSearchFacets() {
  const { available, enabled } = useAiSearch();
  const dispatch = useDispatch();
  const store = useStore();
  const params = useSelector(getQueryParams);
  const [openPill, setOpenPill] = useState<'where' | 'when' | 'guests' | null>(null);
  const close = () => setOpenPill(null);

  // Current values from the URL.
  const address = str(params?.[ESearchFilters.ADDRESS]);
  const dateFrom = str(params?.[ESearchFilters.DATE_FROM]);
  const dateTo = str(params?.[ESearchFilters.DATE_TO]);
  const adults = Number(str(params?.[ESearchFilters.SLEEPS_ADULTS])) || 0;
  const children = Number(str(params?.[ESearchFilters.SLEEPS_CHILDREN])) || 0;
  const guestCount = adults + children;

  // Local in-progress date range so the calendar reflects the first click
  // before both ends are picked (the URL only updates once both are set).
  const [range, setRange] = useState<{ from?: Date; to?: Date }>({});
  useEffect(() => {
    setRange({
      from: dateFrom ? new Date(`${dateFrom}T00:00:00`) : undefined,
      to: dateTo ? new Date(`${dateTo}T00:00:00`) : undefined,
    });
  }, [dateFrom, dateTo]);

  const apply = (filter: Record<string, string | undefined>) => {
    dispatch(applySearchFilter(filter));
  };

  // An autocomplete selection writes to globalHeader; pull the freshest value
  // and commit it (+ mapbox ids) to the search, then close.
  const applyWhere = () => {
    const gh =
      (store.getState() as { globalHeader?: Record<string, string | undefined> }).globalHeader ??
      {};
    apply({
      [ESearchFilters.ADDRESS]: gh.address || undefined,
      [ESearchFilters.MAPBOX_ID]: gh.mapbox_id || undefined,
      [ESearchFilters.FULL_MAPBOX_ADDRESS]: gh.full_mapbox_address || undefined,
    });
    close();
  };

  if (!available || !enabled) return null;

  const whenLabel =
    dateFrom && dateTo
      ? `${dayjs(dateFrom).format('MMM D')} – ${dayjs(dateTo).format('MMM D')}`
      : 'When';
  const guestsLabel = guestCount > 0 ? `${guestCount} guest${guestCount > 1 ? 's' : ''}` : 'Guests';

  return (
    <div className="flex items-center pr-2 mr-2 border-r gap-2 border-neutral-20">
      {/* Where */}
      <PopoverPill
        title={address || 'Where'}
        active={!!address}
        open={openPill === 'where'}
        onToggle={() => setOpenPill(openPill === 'where' ? null : 'where')}
        onClose={close}>
        <WhereAutocomplete onApply={applyWhere} />
      </PopoverPill>

      {/* When */}
      <PopoverPill
        title={whenLabel}
        active={!!(dateFrom && dateTo)}
        open={openPill === 'when'}
        onToggle={() => setOpenPill(openPill === 'when' ? null : 'when')}
        onClose={close}>
        <DatePicker
          range
          numberOfMonths={2}
          disableBefore={new Date()}
          dateFrom={range.from}
          dateTo={range.to}
          onChange={({ from, to }) => {
            setRange({ from, to });
            if (from && to) {
              apply({
                [ESearchFilters.DATE_FROM]: dayjs(from).format('YYYY-MM-DD'),
                [ESearchFilters.DATE_TO]: dayjs(to).format('YYYY-MM-DD'),
              });
              close();
            }
          }}
        />
        <div className="flex justify-end pt-2 border-t border-neutral-20">
          <button
            type="button"
            className="text-sm text-gray-600 underline"
            onClick={() => {
              apply({ [ESearchFilters.DATE_FROM]: undefined, [ESearchFilters.DATE_TO]: undefined });
              close();
            }}>
            Clear dates
          </button>
        </div>
      </PopoverPill>

      {/* Guests */}
      <PopoverPill
        title={guestsLabel}
        active={guestCount > 0}
        open={openPill === 'guests'}
        onToggle={() => setOpenPill(openPill === 'guests' ? null : 'guests')}
        onClose={close}>
        <div className="w-64">
          <GuestStepper
            label="Adults"
            value={adults}
            min={0}
            onChange={n => apply({ [ESearchFilters.SLEEPS_ADULTS]: n > 0 ? String(n) : undefined })}
          />
          <GuestStepper
            label="Children"
            value={children}
            min={0}
            onChange={n =>
              apply({ [ESearchFilters.SLEEPS_CHILDREN]: n > 0 ? String(n) : undefined })
            }
          />
        </div>
      </PopoverPill>
    </div>
  );
}

function GuestStepper({
  label,
  value,
  min,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  onChange: (n: number) => void;
}) {
  return (
    <div className="flex items-center justify-between py-2">
      <span className="text-sm text-gray-900">{label}</span>
      <div className="flex items-center gap-3">
        <button
          type="button"
          aria-label={`Decrease ${label}`}
          disabled={value <= min}
          onClick={() => onChange(value - 1)}
          className="flex items-center justify-center w-8 h-8 text-lg border rounded-full border-neutral-30 disabled:opacity-40">
          −
        </button>
        <span className="w-4 text-sm text-center">{value}</span>
        <button
          type="button"
          aria-label={`Increase ${label}`}
          onClick={() => onChange(value + 1)}
          className="flex items-center justify-center w-8 h-8 text-lg border rounded-full border-neutral-30">
          +
        </button>
      </div>
    </div>
  );
}
